/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Read and reset a managed student's organization credential through FastAPI.
 * @state-owner caller or TanStack Query
 * @allowed-imports @vispo/api-client
 * @forbidden Supabase client; secret persistence; authorization inference
 * @public-api classrooms credential dialog
 * @side-effects authenticated credential disclosure and reset requests
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @tests organization-credentials.test.ts
 */

import type { VispoApiClient } from '@vispo/api-client';
import { z } from 'zod';

export type OrganizationCredentialState =
  | 'ready_unactivated'
  | 'active'
  | 'reset_required'
  | 'disabled';

export interface OrganizationCredential {
  loginCode: string;
  state: OrganizationCredentialState;
}

export interface OrganizationCredentialReset extends OrganizationCredential {
  oneTimeCode: string;
  expiresAt: string;
  activationUrl: string;
}

const credentialSchema = z.object({
  login_code: z.string().min(1),
  state: z.enum(['ready_unactivated', 'active', 'reset_required', 'disabled']),
});

const credentialResetSchema = z.object({
  activation_url: z.string().url(),
  expires_at: z.string().min(1),
  login_code: z.string().min(1),
  one_time_code: z.string().min(1),
});

export async function organizationCredential(
  apiClient: VispoApiClient,
  classroomId: string,
  memberUserId: string,
): Promise<OrganizationCredential> {
  const result = credentialSchema.parse(
    await apiClient.request<unknown>(
      `/api/v1/classrooms/${encodeURIComponent(classroomId)}/members/${encodeURIComponent(memberUserId)}/organization-credential`,
    ),
  );
  return { loginCode: result.login_code, state: result.state };
}

export async function resetOrganizationCredential(
  apiClient: VispoApiClient,
  classroomId: string,
  memberUserId: string,
): Promise<OrganizationCredentialReset> {
  const result = credentialResetSchema.parse(
    await apiClient.request<unknown>(
      `/api/v1/classrooms/${encodeURIComponent(classroomId)}/members/${encodeURIComponent(memberUserId)}/organization-credential/reset`,
      { method: 'POST' },
    ),
  );
  return {
    activationUrl: result.activation_url,
    expiresAt: result.expires_at,
    loginCode: result.login_code,
    oneTimeCode: result.one_time_code,
    state: 'reset_required',
  };
}
